import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { deriveBriefing, readBriefingUrl, WizardApiError } from '../campaign-wizard.api';
import { AdCheckItem } from '../campaign-wizard.types';
import WizAutoTextArea, { TextHighlight } from '../components/wizard-auto-textarea';
import { wz } from '../components/wizard-tokens';
import {
  MiSpinner,
  WizCard,
  WizHeading,
  WizLabel,
  WizInput,
  WizLink,
  WizOutlineButton,
} from '../components/wizard-ui';
import AdCheckPanel from './job/ad-check-panel';
import CloneDialog from './job/clone-dialog';
import LinkDialog from './job/link-dialog';
import WizSelect from './job/wiz-select';
import { canSkipCheck } from './job/job-gate.logic';
import {
  adStatus,
  hasConcreteAdd,
  deriveTriggerKey,
  hasPlaceholders,
  initialDeriveKey,
  insertIntoExtras,
  isAddedToExtras,
  isSingleUrl,
  locationLine,
  missingItems,
  MIN_DERIVE_LENGTH,
  mayReplaceName,
  mayReplaceJobTitle,
  readSuggestedJobTitle,
  rememberSuggestedJobTitle,
  readSuggestedName,
  rememberSuggestedName,
  setLocationLine,
  suggestionOf,
} from './job/job-logic';
import { addLocation } from './candidates/candidates-logic';
import type { WizardStepProps } from './step.types';

/** So lange bleibt ein eingefügter Vorschlag gelb markiert. */
const HIGHLIGHT_MS = 4000;

/** Schritt 1 „Stelle“: Briefing, Anzeigenprüfung, Kampagnenname, Absender. */
const StepJob: React.FC<WizardStepProps> = ({ wizard }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { state, patch, accounts, showBlocker, blocker, nameTaken } = wizard;

  const { gate, runAdCheck: runCheck } = wizard;
  const checking = gate.status === 'checking';
  const checkError = gate.checkFailed ? (
    <>
      {canSkipCheck(gate.checkFailures) ? t('wizGateCheckFailedMany') : t('wizGateCheckFailed')}{' '}
      <WizLink onClick={() => void runCheck(state.briefing)}>{t('wizGateRetry')}</WizLink>
      {canSkipCheck(gate.checkFailures) ? (
        <>
          {' · '}
          <WizLink onClick={wizard.gateSkipCheck}>{t('wizGateSkip')}</WizLink>
        </>
      ) : null}
    </>
  ) : null;
  const briefingBox = useRef<HTMLDivElement>(null);
  const extrasBox = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState<TextHighlight | null>(null);
  const [selection, setSelection] = useState<{ start: number; end: number; tick: number } | null>(
    null
  );
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [readingUrl, setReadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);

  const briefingRef = useRef(state.briefing);
  briefingRef.current = state.briefing;
  const nameRef = useRef(state.name);
  nameRef.current = state.name;
  const jobTitleRef = useRef(state.jobTitle);
  jobTitleRef.current = state.jobTitle;
  const extrasRef = useRef(state.briefingExtras);
  extrasRef.current = state.briefingExtras;
  const [linkOpen, setLinkOpen] = useState(false);
  const lastSuggestion = useRef(readSuggestedName());
  const lastJobTitleSuggestion = useRef(readSuggestedJobTitle());
  // Beim Öffnen mit bereits abgeleitetem Stand nicht erneut ableiten (nur nach einer Änderung).
  const lastDerivedText = useRef(initialDeriveKey(state));

  const status = adStatus(state.adCheck, state, checking);

  const readUrl = useCallback(
    async (url: string): Promise<boolean> => {
      setReadingUrl(true);
      setUrlError(null);
      try {
        const res = await readBriefingUrl(url.trim());
        // Eine neue Anzeige ersetzt die alte komplett: Ergänzungen der vorherigen Stelle würden sonst
        // zu einer anderen Stelle gehören.
        const fields = { briefingExtras: '' };
        setHighlight(null);
        patch({ briefing: res.text, adCheck: null, ...fields });
        void runCheck(res.text, fields);
        return true;
      } catch (e) {
        setUrlError((e as WizardApiError).message || t('wizJobReadUrlError'));
        return false;
      } finally {
        setReadingUrl(false);
      }
    },
    [patch, runCheck, t]
  );

  // „Anzeige ergänzen“ im Popup: Fokus auf das Ergänzungsfeld (dort landen die Vorschläge).
  useEffect(() => {
    if (gate.focusTick > 0) {
      const field = extrasBox.current?.querySelector('textarea');
      field?.focus();
      extrasBox.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [gate.focusTick]);

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    []
  );

  const searchRanRef = useRef(wizard.searchRan);
  searchRanRef.current = wizard.searchRan;

  // Kampagnenname aus dem Briefing vorschlagen, solange der Nutzer ihn nicht selbst gesetzt hat.
  useEffect(() => {
    const text = state.briefing;
    // Ein per „Ergänzen“ eingefügter Jobtitel gehört zum Vorschlag des Kampagnennamens (die Ableitung bekommt die Ergänzungen mit).
    const derivedKey = deriveTriggerKey(text, state.briefingExtras);
    if (
      text.trim().length < MIN_DERIVE_LENGTH ||
      isSingleUrl(text) ||
      derivedKey === lastDerivedText.current
    ) {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      lastDerivedText.current = derivedKey;
      try {
        const derived = await deriveBriefing(
          text,
          { extras: extrasRef.current },
          false
        );
        if (cancelled) return;
        // Jobtitel vorschlagen bzw. nachziehen, solange der Nutzer keinen eigenen eingetragen hat.
        // Nach gelaufener Suche ersetzt der Assistent den Jobtitel nicht mehr von selbst: das würde den Neustart-Dialog auslösen,
        // ohne dass der Nutzer ein Suchfeld angefasst hat. Er ändert ihn dann bewusst selbst.
        if (
          derived.jobTitle &&
          !searchRanRef.current &&
          mayReplaceJobTitle(jobTitleRef.current, lastJobTitleSuggestion.current)
        ) {
          lastJobTitleSuggestion.current = derived.jobTitle;
          rememberSuggestedJobTitle(derived.jobTitle);
          patch({ jobTitle: derived.jobTitle });
        }
        if (!derived.name) return;
        if (mayReplaceName(nameRef.current, lastSuggestion.current)) {
          lastSuggestion.current = derived.name;
          rememberSuggestedName(derived.name);
          patch({ name: derived.name });
        }
      } catch {
        // Der Vorschlag ist eine Hilfe; ohne ihn bleibt das Feld leer.
      }
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [state.briefing, state.briefingExtras, patch]);

  const onBriefingChange = (value: string) => {
    setUrlError(null);
    patch({ briefing: value });
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text');
    const target = e.currentTarget;
    const replacesAll =
      state.briefing.trim() === '' ||
      (target.selectionStart === 0 && target.selectionEnd === state.briefing.length);
    if (replacesAll && isSingleUrl(pasted)) {
      e.preventDefault();
      patch({ briefing: pasted.trim() });
      void readUrl(pasted);
    }
  };

  const onBriefingBlur = () => {
    if (!readingUrl && isSingleUrl(briefingRef.current)) void readUrl(briefingRef.current);
  };

  /** Vorschläge landen im Ergänzungsfeld (nicht in der Anzeige) und werden kurz gelb markiert. */
  const addToExtras = (adds: string[]) => {
    const inserted = insertIntoExtras(state.briefingExtras, adds);
    if (inserted.text === state.briefingExtras) return null;
    patch({ briefingExtras: inserted.text });
    setHighlight({ start: inserted.start, end: inserted.end });
    // Platzhalter „[…]“ markieren (zum Überschreiben), bei einem Leitsatz den Cursor ans Ende setzen.
    setSelection(prev => ({ ...inserted.select, tick: (prev?.tick ?? 0) + 1 }));
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
    extrasBox.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    return inserted.text;
  };

  const fixOne = (item: AdCheckItem) => {
    addToExtras([suggestionOf(item)]);
  };

  const fixAll = () => {
    if (!state.adCheck) return;
    const open = missingItems(state.adCheck.items).filter(i => !isAddedToExtras(state.briefingExtras, i));
    const text = addToExtras(open.map(suggestionOf));
    // Nur mit konkreten KI-Ergänzungen sofort neu prüfen; Leitsätze/Platzhalter muss der Nutzer erst ausfüllen.
    if (text !== null && open.every(hasConcreteAdd) && !hasPlaceholders(text)) {
      void runCheck(state.briefing, { briefingExtras: text });
    }
  };

  // Pflichtfrage „Wo ist die Stelle?“: Antwort als Zeile „Ort: …“ ins Ergänzungsfeld, danach Neuprüfung;
  // ein genannter Ort wird für Schritt 2 vorbelegt (nur wenn dort noch keiner steht).
  const answerLocation = (city: string, remote: boolean) => {
    const extras = setLocationLine(state.briefingExtras, locationLine(city, remote));
    setHighlight(null);
    patch({
      briefingExtras: extras,
      ...(!remote && city && state.locations.length === 0
        ? { locations: addLocation(state.locations, city) }
        : {}),
    });
    void runCheck(state.briefing, { briefingExtras: extras });
  };

  const onExtrasChange = (value: string) => {
    setHighlight(null);
    patch({ briefingExtras: value });
  };

  const briefingInvalid = showBlocker && blocker === 'briefing';
  const nameInvalid = nameTaken || (showBlocker && blocker === 'name');
  const accountInvalid = showBlocker && blocker === 'account';

  const onNameChange = (value: string) => {
    lastSuggestion.current = '';
    rememberSuggestedName('');
    patch({ name: value });
  };

  const onJobTitleChange = (jobTitle: string) => {
    lastJobTitleSuggestion.current = '';
    rememberSuggestedJobTitle('');
    patch({ jobTitle });
  };

  return (
    <>
      <WizCard gap={24}>
        <WizHeading title={t('wizJobTitle')} subtitle={t('wizJobSubtitle')} />
        <div className={classes.block} ref={briefingBox}>
          <div className={classes.labelRow}>
            <WizLabel>{t('wizJobDescriptionLabel')}</WizLabel>
            <WizOutlineButton
              onClick={() => {
                setUrlError(null);
                setLinkOpen(true);
              }}
              icon={<LinkIcon sx={{ fontSize: 16 }} />}
            >
              {t('wizJobLinkButton')}
            </WizOutlineButton>
          </div>
          <WizAutoTextArea
            value={state.briefing}
            onChange={onBriefingChange}
            onPaste={onPaste}
            onBlur={onBriefingBlur}
            minRows={6}
            placeholder={t('wizJobDescriptionPlaceholder')}
            invalid={briefingInvalid}
            aria-label={t('wizJobDescriptionLabel')}
          />
          <div className={classes.hint}>
            {readingUrl && !linkOpen ? <MiSpinner size={16} /> : null}
            {readingUrl && !linkOpen ? t('wizJobReadingUrl') : null}
          </div>
          {urlError && !linkOpen ? <div className={classes.error}>{urlError}</div> : null}
          <div className={classes.notice} role="note">
            <InfoOutlinedIcon sx={{ fontSize: 20, color: wz.primary, flexShrink: 0 }} />
            <span>{t('wizJobNotSentHint')}</span>
          </div>
        </div>
        <div className={classes.block} ref={extrasBox}>
          <WizLabel>{t('wizJobExtrasLabel')}</WizLabel>
          <WizAutoTextArea
            value={state.briefingExtras}
            onChange={onExtrasChange}
            highlight={highlight}
            select={selection}
            minRows={3}
            placeholder={t('wizJobExtrasPlaceholder')}
            aria-label={t('wizJobExtrasLabel')}
          />
          <div className={classes.hint}>{t('wizJobExtrasHint')}</div>
          <AdCheckPanel
            status={status}
            adCheck={state.adCheck}
            extras={state.briefingExtras}
            canCheck={state.briefing.trim().length > 0 && !readingUrl}
            error={checkError}
            highlightMissing={gate.highlight}
            onCheck={() => void runCheck(state.briefing)}
            onFixAll={fixAll}
            onFixOne={fixOne}
            onLocation={answerLocation}
            locationFocusTick={gate.locationTick}
          />
        </div>
        <div className={classes.grid}>
          <div className={classes.block}>
            <WizLabel>{t('wizJobNameLabel')}</WizLabel>
            <WizInput
              value={state.name}
              onChange={onNameChange}
              invalid={nameInvalid}
              placeholder={t('wizJobNamePlaceholder')}
              aria-label={t('wizJobNameLabel')}
            />
            {nameTaken ? <div className={classes.error}>{t('wizNameTaken')}</div> : null}
          </div>
          <div className={classes.block}>
            <WizLabel>{t('wizJobTitleFieldLabel')}</WizLabel>
            <WizInput
              value={state.jobTitle}
              onChange={onJobTitleChange}
              placeholder={t('wizJobTitleFieldPlaceholder')}
              aria-label={t('wizJobTitleFieldLabel')}
            />
            <div className={classes.hint}>{t('wizJobTitleFieldHint')}</div>
          </div>
          <div className={classes.block}>
            <WizLabel>{t('wizJobAccountLabel')}</WizLabel>
            <WizSelect
              value={state.accountId}
              options={accounts.map(a => ({
                value: a.id,
                label:
                  a.name && accounts.filter(o => o.name === a.name).length > 1
                    ? `${a.name} (${a.email})`
                    : a.name || a.email,
                sub: a.name ? a.email : undefined,
              }))}
              placeholder={
                accounts.length === 0 && !wizard.accountsLoading
                  ? t('wizNoAccount')
                  : t('wizJobAccountPlaceholder')
              }
              onChange={accountId => patch({ accountId })}
              invalid={accountInvalid}
              ariaLabel={t('wizJobAccountLabel')}
            />
          </div>
        </div>
      </WizCard>
      <div className={classes.similar}>
        <ContentCopyIcon sx={{ fontSize: 18 }} />
        <span>{t('wizJobSimilar')}</span>
        <WizLink onClick={() => setCloneOpen(true)}>{t('wizJobUseTemplate')}</WizLink>
      </div>
      <LinkDialog
        open={linkOpen}
        hasText={state.briefing.trim().length > 0}
        reading={readingUrl}
        error={urlError}
        onClose={() => setLinkOpen(false)}
        onSubmit={async url => {
          if (await readUrl(url)) setLinkOpen(false);
        }}
      />
      <CloneDialog
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        accountId={state.accountId}
        briefing={state.briefing}
        onCreated={wizard.adoptCampaign}
      />
    </>
  );
};

const useStyles = makeStyles()({
  block: { display: 'flex', flexDirection: 'column', gap: 8 },
  labelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  hint: { fontSize: 13, color: wz.muted, display: 'flex', alignItems: 'center', gap: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20 },
  error: { fontSize: 13, color: wz.redFg },
  notice: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    fontWeight: 600,
    color: wz.textDim,
    background: wz.primarySoft,
    borderLeft: `3px solid ${wz.primary}`,
    borderRadius: 8,
    padding: '10px 14px',
  },
  similar: {
    fontSize: 14,
    color: wz.muted,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 4px',
  },
});

export default StepJob;
